<template>
  <FormField :label="label" :optional="optional" :error="showError ? errorMsg : undefined">
    <input
      :value="modelValue"
      type="tel"
      inputmode="tel"
      autocomplete="off"
      :placeholder="placeholder"
      @input="onInput"
      @blur="touched = true"
    />
  </FormField>
</template>

<script setup lang="ts">
/**
 * Shared phone input with inline validation, following the ClientsView
 * touched-on-blur pattern (error shown only after the field is blurred).
 *
 * - v-model carries the RAW string (never normalised/E.164'd — kept as typed).
 * - Emits `update:valid` with the current validity so the PARENT can gate its
 *   submit button. Validity is: blank-when-optional is valid; otherwise the
 *   shared validatePhone() decides. This matches the existing contract where an
 *   empty optional phone is allowed but a non-empty bad one blocks save.
 * - The error only RENDERS after blur, but `update:valid` fires immediately so
 *   submit-gating is correct even before the user blurs.
 *
 * The label/error chrome is delegated to the shared FormField; the visible
 * behaviour and the v-model + update:valid contract are UNCHANGED.
 */
import { computed, ref, watch } from 'vue'
import { validatePhone } from '@/lib/validatePhone'
import FormField from '@/components/FormField.vue'

const props = withDefaults(
  defineProps<{
    modelValue: string
    label?: string
    optional?: boolean
    placeholder?: string
  }>(),
  {
    label: 'Phone',
    optional: true,
    placeholder: '+1 (555) 123-4567',
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'update:valid', valid: boolean): void
}>()

const touched = ref(false)

// Validity the parent cares about: an optional+blank phone is valid; otherwise
// the shared rule decides.
const isValid = computed(() => {
  const v = (props.modelValue ?? '').trim()
  if (!v) return props.optional
  return validatePhone(v).ok
})

const errorMsg = computed(() => {
  const v = (props.modelValue ?? '').trim()
  if (!v) return props.optional ? '' : 'Phone number is required'
  return validatePhone(v).error ?? ''
})

const showError = computed(() => touched.value && !isValid.value && !!errorMsg.value)

function onInput(e: Event) {
  emit('update:modelValue', (e.target as HTMLInputElement).value)
}

// Keep the parent's gate in sync. immediate so initial state is reported.
watch(isValid, (v) => emit('update:valid', v), { immediate: true })
</script>
